import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { validationMessages } from 'constants/validation'
import {
	Stack, StackProps,	
	Rating,
	TextField,
	Autocomplete,
	Box
} from '@mui/material'

import { Section } from 'components/controls/section'

import { SearchsGuideLineFormData } from 'interface/search/guideLine'
import { useListGuideLine } from 'interface/guideLine'
import { Loading } from 'components/controls/loading'

const rateList = [
  { rate: 0.2, value: 1, description: '星1' },
  { rate: 0.5, value: 2, description: '星2' },
  { rate: 1.0, value: 3, description: '星3' },
  { rate: 2.0, value: 4, description: '星4' },
  { rate: 3.0, value: 5, description: '星5' },
]

const {
  Required
} = validationMessages

const formSchema = yup.object().shape({
  guideLineId: yup.number().required(Required).min(1, Required),
	rate: yup.number().required(Required)
})

type useSearchsGuideLineFormProps = {
	searchId: number
	data?: SearchsGuideLineFormData
	onSubmit: (data: SearchsGuideLineFormData) => void
}
export const useSearchsGuideLineForm = ({
	data,
	onSubmit
}: useSearchsGuideLineFormProps) => {
	const guideLineListState = useListGuideLine()
	const form = useForm({
		defaultValues: data ? {
			guideLineId: data.guideLineId,
			rate: data.rate ?? 1
		} : {
			guideLineId: 0,
			rate: 1
		},
		mode: 'onChange',
		resolver: yupResolver(formSchema)
	})
	return {
		...form,
		guideLineListState,
		submit: form.handleSubmit(input => onSubmit({
			guideLineId: input.guideLineId,
			rate: input.rate,
			updateTime: data?.updateTime
		}))
	}
}

export const SearchsGuideLineForm = ({
	control,
	setValue,
	formState: { errors },
	guideLineListState,
	...others
}: ReturnType<typeof useSearchsGuideLineForm> & StackProps) => {
	return (
		<>
      { guideLineListState.loading && <Loading /> }

			<Stack direction="column" {...others}>
				<Section label='ガイドライン'labelProps={{ paddingY: 2 }}>
					<Controller
						control={control}
						name="guideLineId"
						render={({ field }) => (
							<>
								<Autocomplete<number>
									{...field}
									size='small'
									options={guideLineListState.data?.list.map(master => master.guideLineId) || []}
									getOptionLabel={
										id => guideLineListState.data?.list.find(
											master => master.guideLineId === id
										)?.guideLineName ?? ''
									}
									renderInput={(params) => (
										<TextField
											{...params}
											error={!!errors.guideLineId}
											helperText={errors.guideLineId ? errors.guideLineId.message : ''}
										/>
									)}
									renderOption={(props, id) => {
										const record = guideLineListState.data?.list.find(master => master.guideLineId === id)
										return record ? (
											<Box component="li" {...props}>
												{record.guideLineName}
											</Box>
										) : <></>
									}}
									onChange={(_, value) => {
										setValue("guideLineId", value ?? 0, { shouldValidate: true })
									}}
								/>
							</>
						)}
					/>
				</Section>

				<Section label="重要度" labelProps={{ paddingY: 2 }}>
					<Controller
						control={control}
						name="rate"
						render={({ field }) => (
							<Rating
								value={rateList.find(r => r.rate === field.value)?.value ?? 0}
								name="rate"
								onChange={(e, v) => field.onChange(e, rateList.find(r => r.value === v)?.rate ?? 10)}
							/>
						)}
					/>
				</Section>
			</Stack>
		</>
	)
}